import { JwtPayload, jwtDecode } from 'jwt-decode';
import { TPlainObject } from './types';

export type TPostResult<T> = {
  body: T;
  status: number;
  error: string | null;
  aborted: boolean;
};

export type TRequestConfig = RequestInit & { json?: TPlainObject };

/**
 * Carries the HTTP status alongside the body.
 *
 * Rejecting with a bare string threw the status away, which is why nothing in the app
 * could tell an expired session from a validation error -- and therefore why a dead
 * session never sent anyone back to sign in.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: string,
  ) {
    super(body || `Request failed with status ${status}`);
    this.name = 'ApiError';
  }
}

const prefix = `/api/backoffice`;
const key = `at`;

let accessToken: string | null;
let accessPayload: JwtPayload | null;
let rotation: Promise<string | null> | null;

/**
 * The session is gone and the user has to sign in again.
 *
 * Kept as a notification rather than a redirect so the app can ask them to sign in over
 * whatever they were doing, without unmounting the page and discarding unsaved work.
 */
type SessionExpiredListener = () => void;
const sessionExpiredListeners = new Set<SessionExpiredListener>();
let sessionIsExpired = false;

export const onSessionExpired = (fn: SessionExpiredListener) => {
  sessionExpiredListeners.add(fn);
  // A request can fail before React has mounted and subscribed. Without replaying the
  // current state the notification is lost, and the flag below then suppresses every
  // later one — leaving exactly the silent dead session this is meant to end.
  if (sessionIsExpired) fn();
  return () => sessionExpiredListeners.delete(fn);
};

const notifySessionExpired = () => {
  // The stored token is dead; leaving it behind makes the next page load render a
  // signed-in shell that cannot do anything.
  storeAccessToken(null);
  if (sessionIsExpired) return;
  sessionIsExpired = true;
  sessionExpiredListeners.forEach((fn) => fn());
};

export const clearSessionExpired = () => {
  sessionIsExpired = false;
};

const objectToSearchParams = (obj: TPlainObject): string => {
  const params = new URLSearchParams();

  const addParam = (key: string, value: any) => {
    if (value === null || value === undefined) {
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((item) => params.append(key, String(item)));
    } else if (value instanceof Date) {
      // Handle Date objects by converting to ISO string
      params.append(key, value.toISOString());
    } else if (typeof value === 'object' && value !== null) {
      // Handle nested objects by flattening them
      for (const [subKey, subValue] of Object.entries(value)) {
        addParam(`${key}.${subKey}`, subValue);
      }
    } else {
      params.append(key, String(value));
    }
  };

  for (const [key, value] of Object.entries(obj)) {
    addParam(key, value);
  }

  return params.toString();
};

export const storeAccessToken = (token: string | null) => {
  if (token) {
    accessPayload = jwtDecode(token);
    accessToken = token;
    localStorage.setItem(key, accessToken);
  } else {
    accessToken = null;
    accessPayload = null;
    localStorage.removeItem(key);
  }
};

export const shouldRotateToken = () =>
  (accessPayload?.exp ?? 0) < Date.now() / 1000;

export const tokenExists = () => !!accessToken;

/**
 * @param force rotate even if this browser thinks the token is still valid. The server
 *   is the authority on expiry, and a client clock a few seconds fast or slow is enough
 *   to disagree with it -- so a 401 forces a rotation rather than trusting `exp` here.
 */
const rotateAccessToken = (force = false): Promise<string | null> => {
  if (rotation) {
    return rotation;
  }

  if (!accessToken) {
    return Promise.resolve(null);
  }

  if (!force && !shouldRotateToken()) {
    return Promise.resolve(accessToken);
  }

  return (rotation = exec(`user/rotate`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  })
    .then((res) => res.text())
    .then((t) => {
      storeAccessToken(t);
      return t;
    })
    // Only the credential actually being rejected ends the session. A refused rotation
    // used to reject silently and nothing acted on it, so the app kept rendering as
    // signed in while every request failed -- but treating *every* failure as expiry is
    // the opposite mistake: a dropped connection or a 500 would sign out a user whose
    // session is perfectly good. Those stay retryable errors.
    .catch((e: unknown) => {
      const status = e instanceof ApiError ? e.status : 0;
      if (status === 401 || status === 403 || status === 404) {
        notifySessionExpired();
        return null;
      }
      throw e;
    })
    .finally(() => (rotation = null)));
};

const exec = async <T = void>(
  url: string,
  cfg: TRequestConfig = {},
): Promise<Omit<Response, 'json'> & { json: () => Promise<T> }> => {
  let { body } = cfg;
  const { json = {}, ...init } = cfg;
  let response;
  url = `${prefix}/${url}`;
  init.method ??= 'POST';
  if (init.method === 'GET' || init.method === 'HEAD') {
    url += '?' + objectToSearchParams(json);
  } else {
    body = body || JSON.stringify(json);
    // Only set Content-Type for JSON, let browser handle FormData
    if (!(body instanceof FormData)) {
      init.headers = { ...init.headers, 'Content-Type': 'application/json' };
    }
  }

  try {
    response = await fetch(url, { ...init, body });
  } catch (e) {
    // Network-level failure: no status to report.
    return Promise.reject(new ApiError(0, (e as Error).message || String(e)));
  }
  if (!response.ok) {
    return Promise.reject(new ApiError(response.status, await response.text()));
  }
  return Promise.resolve(response);
};

const withAuth = (cfg: TRequestConfig, token: string): TRequestConfig => ({
  ...cfg,
  headers: { ...cfg.headers, Authorization: `Bearer ${token}` },
});

export const call = async <T>(
  url: string,
  cfg: TRequestConfig = {},
  pub = false,
) => {
  if (pub) return exec<T>(url, cfg);

  const token = await rotateAccessToken();
  if (!token) {
    notifySessionExpired();
    return Promise.reject(new ApiError(401, 'Your session has expired.'));
  }

  try {
    return await exec<T>(url, withAuth(cfg, token));
  } catch (e) {
    // Only an authentication failure gets a second chance. A 403 is a permission
    // decision about a live session and must never sign anybody out.
    if (!(e instanceof ApiError) || e.status !== 401) throw e;

    // Pages fire many requests at once, so several can fail on the same stale token.
    // If another one has already rotated while this was in flight, use what it got:
    // asking the server to rotate a token it just issued would be refused as premature,
    // and that refusal would read as an expired session for everybody.
    //
    // Otherwise rotate on the server's terms — a clock a little out of step is enough
    // for it to reject a token this browser believed was valid.
    const fresh =
      accessToken && accessToken !== token
        ? accessToken
        : await rotateAccessToken(true);
    if (!fresh) {
      return Promise.reject(new ApiError(401, 'Your session has expired.'));
    }

    try {
      return await exec<T>(url, withAuth(cfg, fresh));
    } catch (retry) {
      if (retry instanceof ApiError && retry.status === 401) {
        notifySessionExpired();
        return Promise.reject(new ApiError(401, 'Your session has expired.'));
      }
      throw retry;
    }
  }
};

storeAccessToken(localStorage.getItem(key));

// Admin API methods
import type {
  CreateRoleDto,
  PaginatedUsers,
  Permission,
  Role,
  UpdateRoleDto,
  UpdateUserDto,
  UserDetails,
} from '@/types/admin';

/**
 * Get paginated list of users
 * Note: This function is deprecated. Use useAdminUsers hook from @/models/adminUsers instead.
 */
export const getUsers = async (): Promise<PaginatedUsers> => {
  const response = await call<PaginatedUsers>('admin/users/list', {
    method: 'GET',
  });
  return response.json();
};

/**
 * Get detailed user information
 */
export const getUserDetails = async (userId: number): Promise<UserDetails> => {
  const response = await call<UserDetails>(`admin/users/${userId}`, {
    method: 'GET',
  });
  return response.json();
};

/**
 * Update user information
 */
export const updateUser = async (
  data: UpdateUserDto,
): Promise<{ success: boolean; user: any }> => {
  const response = await call<{ success: boolean; user: any }>(
    'admin/users/update',
    {
      json: data,
    },
  );
  return response.json();
};

/**
 * Get all roles
 */
export const getRoles = async (): Promise<{ roles: Role[] }> => {
  const response = await call<{ roles: Role[] }>('rbac/roles', {
    method: 'GET',
  });
  return response.json();
};

/**
 * Create a new role
 */
export const createRole = async (
  data: CreateRoleDto,
): Promise<{ success: boolean; role: Role }> => {
  const response = await call<{ success: boolean; role: Role }>(
    'rbac/roles/create',
    {
      method: 'POST',
      json: data,
    },
  );
  return response.json();
};

/**
 * Update an existing role
 */
export const updateRole = async (
  data: UpdateRoleDto,
): Promise<{ success: boolean; role: Role }> => {
  const response = await call<{ success: boolean; role: Role }>(
    'rbac/roles/update',
    {
      method: 'POST',
      json: data,
    },
  );
  return response.json();
};

/**
 * Delete a role
 */
export const deleteRole = async (
  roleId: number,
): Promise<{ success: boolean; message: string }> => {
  const response = await call<{ success: boolean; message: string }>(
    'rbac/roles/delete',
    {
      method: 'POST',
      json: { roleId },
    },
  );
  return response.json();
};

/**
 * Get roles assigned to a user
 */
export const getUserRoles = async (
  userId: number,
): Promise<{ userId: number; roles: any[] }> => {
  const response = await call<{ userId: number; roles: any[] }>(
    `rbac/user-roles/${userId}`,
    {
      method: 'GET',
    },
  );
  return response.json();
};

/**
 * Assign a role to a user
 */
export const assignUserRole = async (
  userId: number,
  roleId: number,
): Promise<{ success: boolean; message: string }> => {
  const response = await call<{ success: boolean; message: string }>(
    'rbac/user-roles/assign',
    {
      method: 'POST',
      json: { userId, roleId },
    },
  );
  return response.json();
};

/**
 * Revoke a role from a user
 */
export const revokeUserRole = async (
  userId: number,
  roleId: number,
): Promise<{ success: boolean; message: string }> => {
  const response = await call<{ success: boolean; message: string }>(
    'rbac/user-roles/revoke',
    {
      method: 'POST',
      json: { userId, roleId },
    },
  );
  return response.json();
};

/**
 * Get all permissions
 */
export const getAllPermissions = async (): Promise<{
  permissions: Permission[];
}> => {
  const response = await call<{ permissions: Permission[] }>(
    'rbac/permissions',
    {
      method: 'GET',
    },
  );
  return response.json();
};

/**
 * Get user-specific permissions (grants and revokes)
 */
export const getUserPermissions = async (userId: number): Promise<any> => {
  const response = await call<any>(`rbac/user-permissions/${userId}`, {
    method: 'GET',
  });
  return response.json();
};

/**
 * Grant a permission to a user
 */
export const grantUserPermission = async (
  userId: number,
  permissionName: string,
): Promise<{ success: boolean; message: string }> => {
  const response = await call<{ success: boolean; message: string }>(
    'rbac/user-permissions/grant',
    {
      method: 'POST',
      json: { userId, permissionName },
    },
  );
  return response.json();
};

/**
 * Revoke a permission from a user
 */
export const revokeUserPermission = async (
  userId: number,
  permissionName: string,
): Promise<{ success: boolean; message: string }> => {
  const response = await call<{ success: boolean; message: string }>(
    'rbac/user-permissions/revoke',
    {
      method: 'POST',
      json: { userId, permissionName },
    },
  );
  return response.json();
};

/**
 * Remove explicit permission override (grant or revoke)
 */
export const removeUserPermissionOverride = async (
  userId: number,
  permissionName: string,
): Promise<{ success: boolean; message: string }> => {
  const response = await call<{ success: boolean; message: string }>(
    'rbac/user-permissions/remove',
    {
      method: 'POST',
      json: { userId, permissionName },
    },
  );
  return response.json();
};
